import { useId, useState } from 'react'
import { Button } from '../../components/ui/Button'
import { Checkbox } from '../../components/ui/Checkbox'
import { Chip } from '../../components/ui/Chip'
import { RadioGroup } from '../../components/ui/Radio'
import { PlusGlyph } from '../../components/ui/lucide-glyphs'
import { useRangeSelection } from '../../lib/list/useRangeSelection'
import { SERVICE_TYPE_OPTIONS } from './review-domain'
import type { RfqReviewLine, RfqReviewService } from './review-domain'

/** Every `SERVICE_TYPE_OPTIONS` value is a real `<option>` in the design's
 * dropdown (`dc.html:4295-4296`), keyed by its wire value so a change event's
 * `event.target.value` maps straight back without re-deriving anything. */
const SERVICE_TYPE_BY_VALUE = new Map(SERVICE_TYPE_OPTIONS.map((option) => [option.value, option]))

const SCOPE_OPTIONS: readonly {
  readonly value: RfqReviewService['scope']
  readonly label: string
}[] = [
  { value: 'entire_package', label: 'Entire package' },
  { value: 'specific_parts', label: 'Specific parts' },
]

const SERVICE_TYPE_LABEL: Record<RfqReviewService['type'], string> = Object.fromEntries(
  SERVICE_TYPE_OPTIONS.map((option) => [option.value, option.label]),
) as Record<RfqReviewService['type'], string>

export interface AddOnServicesCardProps {
  readonly lines: readonly RfqReviewLine[]
  readonly services: readonly RfqReviewService[]
  readonly onAdd: (service: RfqReviewService) => void
  readonly onRemove: (localId: string) => void
  readonly noServicesAck: boolean
  readonly onNoServicesAckChange: (next: boolean) => void
}

function scopeSummary(service: RfqReviewService, lines: readonly RfqReviewLine[]): string {
  if (service.scope === 'entire_package') return 'Entire package'
  const names = service.sourceableIds
    .map((id) => lines.find((line) => line.id === id)?.name)
    .filter((name): name is string => Boolean(name))
  return names.length > 0 ? `Specific parts: ${names.join(', ')}` : 'Specific parts'
}

/**
 * RFQ review screen's add-on services block (screens.md §15 / components.md
 * "Add-on service card"): a purple card per added service (label, "Quote
 * required" badge, scope, remove ×) plus an add-form variant (a native
 * `<select>` for the service type — see UID-401 item 1 doc comment on the
 * select below for why this SUPERSEDES UID-215/ADR-0086's accessible radio
 * card-list — conditional description, entire-package/specific-parts scope
 * toggle with a specific-parts checklist when that scope is chosen).
 *
 * UID-359 (item 4) — three design-fidelity corrections:
 *
 *  1. NOT A CARD. See `TargetingCard`'s doc comment: the design's expanded
 *     body is one flat region and this is its second half, introduced by a
 *     13px/700 label row (`dc.html:4273`) rather than a big `<h2>`. The row
 *     also carries the design's "{n} added" count on the right once at
 *     least one service exists.
 *
 *     UID-359 Gate-5 (WCAG 1.3.1) — de-carding had dropped this label to a
 *     plain `<span>`, deleting both the region's only heading AND its
 *     accessible name — a screen-reader user navigating by heading could no
 *     longer reach this block at all. The label is now an `<h3 className=
 *     "rfq-services-label">` (same class, so still 13px/700 — `global.css`'s
 *     app-wide `h1..h6 { margin: 0; font-family: var(--font-family-heading) }`
 *     reset already zeroes the UA heading margin and Museo Sans now equals
 *     the body font post-ADR-0094, so promoting the tag changes nothing
 *     visually), and the wrapping block restores a programmatic name via
 *     `aria-labelledby`, matching this app's own `Card aria-labelledby` +
 *     `<h2 id=…>` convention elsewhere on this page (`RfqPartsCard`,
 *     `RequestSummaryRail`). `<h3>` (not `<h2>`): this block sits nested
 *     inside the page's collapsible "Request details & services" section,
 *     which occupies the h2 slot conceptually alongside this page's other
 *     h2 siblings ("Parts in this request", "Request summary") even though
 *     its own title is a disclosure `<button>` rather than a literal
 *     heading (`RequestDetailsSection.tsx` — out of this fix's scope to
 *     change). `<h3>` reflects that one-level-deeper nesting without
 *     introducing a phantom level.
 *  2. The add affordance is the design's full-width 42px DASHED LAVENDER button
 *     reading "Add a service" with a leading `+` (`dc.html:4320`) — not a plain
 *     outline `Button`. It is the region's primary invitation and the comp gives
 *     it the whole width; the shared `Button` primitive has no dashed variant
 *     and adding one app-wide for a single consumer would be worse than a
 *     scoped class.
 *  3. The "No additional services needed" acknowledgment renders BELOW that
 *     button (`dc.html:4323`, `margin-top:11px`), not above it. Order matters
 *     here beyond pixels: the design leads with the action and offers the
 *     opt-out as the fallback, where the built order led with the opt-out.
 */
export function AddOnServicesCard({
  lines,
  services,
  onAdd,
  onRemove,
  noServicesAck,
  onNoServicesAckChange,
}: AddOnServicesCardProps) {
  const [formOpen, setFormOpen] = useState(false)
  const [type, setType] = useState<RfqReviewService['type']>('assembly')
  const [description, setDescription] = useState('')
  const [scope, setScope] = useState<RfqReviewService['scope']>('entire_package')
  // UID-610 — `useRangeSelection` additionally carries the Gmail/Finder
  // shift-click anchor.
  const rangeSelection = useRangeSelection()
  const { selectedIds } = rangeSelection
  const typeId = useId()
  const descriptionId = useId()
  const servicesHeadingId = useId()
  const typeSubtitleId = useId()
  const selectedTypeOption = SERVICE_TYPE_BY_VALUE.get(type)

  function resetForm() {
    setType('assembly')
    setDescription('')
    setScope('entire_package')
    rangeSelection.clear()
  }

  function handleTypeChange(nextType: RfqReviewService['type']) {
    setType(nextType)
    // The description field only renders for "Custom service" (the design's
    // "Describe what you need") — clear any typed text once it's unmounted so
    // switching away and back never resurrects a description that no longer
    // belongs to the selected type, and a non-"custom" service is never
    // submitted carrying leftover text.
    if (nextType !== 'custom') {
      setDescription('')
    }
  }

  /** UID-610 — shift-click range selection over the "Specific parts"
   * checklist's rendered row order (Gmail/Finder semantics, same as
   * UID-470's Parts Library). `shiftKey` reaches here from the row
   * checkbox's `onClick`, not `onChange` (a `ChangeEvent` carries no
   * modifier-key state). */
  function toggleLine(id: string, shiftKey: boolean) {
    rangeSelection.toggle(
      id,
      lines.map((line) => line.id),
      shiftKey,
    )
  }

  function handleAdd() {
    onAdd({
      localId:
        typeof crypto !== 'undefined' && 'randomUUID' in crypto
          ? crypto.randomUUID()
          : `service-${Date.now()}-${Math.random()}`,
      type,
      // Belt-and-braces alongside `handleTypeChange`'s clear-on-switch: a
      // non-"custom" service never carries a description, even if this ever
      // gets called from a path that skipped that handler.
      description: type === 'custom' ? description : '',
      scope,
      sourceableIds: scope === 'specific_parts' ? [...selectedIds] : [],
    })
    resetForm()
    setFormOpen(false)
  }

  const scopeSatisfied = scope === 'entire_package' || selectedIds.size > 0
  // "Custom service" requires a non-empty description (the design's
  // "Describe what you need"); every other type needs none.
  const descriptionSatisfied = type !== 'custom' || description.trim().length > 0
  const canAdd = scopeSatisfied && descriptionSatisfied

  return (
    <div className="rfq-services-block" aria-labelledby={servicesHeadingId}>
      {/* `dc.html:4273` — a 13px/700 label with the added-count pushed right.
       * UID-359 Gate-5 — a real `<h3>` (not a `<span>`), so this region has a
       * heading reachable by screen-reader heading navigation again; same
       * class, so still 13px/700 (see doc comment above). */}
      <div className="rfq-services-label-row">
        <h3 id={servicesHeadingId} className="rfq-services-label">
          Add-on services
        </h3>
        {services.length > 0 && (
          <span className="rfq-services-added-count">{services.length} added</span>
        )}
      </div>
      <p className="rfq-services-subtitle">
        Assembly, inserts, finishing and more — quoted alongside your parts as one package.
      </p>

      {services.length > 0 && (
        <ul className="rfq-services-list">
          {services.map((service) => (
            <li key={service.localId} className="rfq-service-added-card">
              {/* UID-401 item 3(b) (`dc.html:4282`) — the design puts the
               * "Quote required" badge INLINE right after the label on the
               * same line (`margin-left:4px`), not right-justified in a
               * separate header row. Both now live in one label span; the
               * badge keeps the shared `Chip tone="purple"` treatment (only
               * PLACEMENT was in the ticket's match list, not badge color —
               * see this fix's report). */}
              <span className="rfq-service-added-label">
                {SERVICE_TYPE_LABEL[service.type]}
                <Chip tone="purple">Quote required</Chip>
              </span>
              {/* `dc.html:4283` — "Applies to: {{ sv.scopeLabel }}", not the
               * scope alone. */}
              <p className="rfq-service-added-scope">Applies to: {scopeSummary(service, lines)}</p>
              {service.description && (
                // `dc.html:4284` — the description renders in curly quotes,
                // italic, `color:var(--fg-3)` (this file's `.rfq-service-
                // added-note` override at the end of rfq.css moves it off
                // `--color-text-secondary` onto the `--fg-3`-equivalent
                // `--color-text-tertiary`).
                <p className="rfq-service-added-note">“{service.description}”</p>
              )}
              <button
                type="button"
                className="btn-link"
                aria-label={`Remove ${SERVICE_TYPE_LABEL[service.type]} service`}
                onClick={() => onRemove(service.localId)}
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}

      {!formOpen && (
        <button type="button" className="rfq-add-service-btn" onClick={() => setFormOpen(true)}>
          <PlusGlyph aria-hidden="true" />
          Add a service
        </button>
      )}

      {formOpen && (
        <div className="rfq-add-service-form">
          {/* UID-401 item 1 — the design (`dc.html:4294-4297`) is a native
           * `<select>` ("Service"), not the UID-215/ADR-0086 accessible
           * radio card-list that used to render every option's subtitle at
           * once. Converting collapses that always-visible subtitle exposure
           * — a `<select>`'s `<option>` text is label-only in every browser,
           * there is no markup that keeps a second line inside an option.
           *
           * SUBTITLE ACCESSIBILITY DECISION (Gate 5 — read before touching
           * this again): rather than silently dropping the subtitle copy,
           * the subtitle of the CURRENTLY SELECTED option renders as a
           * hint paragraph directly under the select, programmatically tied
           * to it via `aria-describedby`. A sighted user sees it inline
           * (same information density the design's own "Custom service"
           * description-hint pattern already established below); a
           * screen-reader user gets it as part of the field's accessible
           * description WCAG 1.3.1/3.3.2 requires some programmatic
           * association for, which is strictly more than the design itself
           * offers (the design has no subtitle-hint element at all — the six
           * options it lists have self-explanatory labels; this app carries
           * two design-silent extras, `certification`/`expedite`, that
           * genuinely have no subtitle to show, exactly as before). This is
           * an ADDITION beyond the design, not a fidelity match — flagged in
           * this fix's report for the record.
           *
           * BUSINESS-CALL NOTE (do not resolve here) — `SERVICE_TYPE_OPTIONS`
           * (`review-domain.ts`, owned elsewhere) still carries all 8 wire
           * values though the design lists only 6; `certification`/
           * `expedite` stay rendered, unchanged from before this fix. See
           * this fix's report for the escalation. */}
          <div className="form-field">
            <label htmlFor={typeId} className="form-field-label">
              Service type
            </label>
            <select
              id={typeId}
              className="rfq-service-type-select"
              value={type}
              aria-describedby={selectedTypeOption?.subtitle ? typeSubtitleId : undefined}
              onChange={(event) => handleTypeChange(event.target.value as RfqReviewService['type'])}
            >
              {SERVICE_TYPE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            {selectedTypeOption?.subtitle && (
              <p id={typeSubtitleId} className="form-field-hint">
                {selectedTypeOption.subtitle}
              </p>
            )}
          </div>

          {type === 'custom' && (
            <div className="form-field">
              <label htmlFor={descriptionId} className="form-field-label">
                Description
              </label>
              <textarea
                id={descriptionId}
                className="form-field-input"
                maxLength={500}
                rows={3}
                value={description}
                onChange={(event) => setDescription(event.target.value)}
              />
            </div>
          )}

          <RadioGroup
            name="rfq-service-scope"
            legend="Applies to"
            options={SCOPE_OPTIONS}
            value={scope}
            onChange={setScope}
          />

          {scope === 'specific_parts' && (
            <fieldset className="rfq-service-parts-checklist">
              <legend className="sr-only">Which parts does this service apply to?</legend>
              {lines.length === 0 ? (
                <p className="form-field-hint">Add parts to the request first.</p>
              ) : (
                <ul>
                  {lines.map((line) => {
                    const checklistId = `${typeId}-${line.id}`
                    return (
                      <li key={line.id}>
                        <label htmlFor={checklistId}>
                          <input
                            id={checklistId}
                            type="checkbox"
                            checked={selectedIds.has(line.id)}
                            // UID-610 — reads `event.shiftKey` off
                            // `onClick`; `onChange` carries no
                            // modifier-key state, so it stays a no-op
                            // (see `toggleLine`'s doc comment).
                            onClick={(event) => toggleLine(line.id, event.shiftKey)}
                            onChange={() => {}}
                          />
                          {line.name}
                        </label>
                      </li>
                    )
                  })}
                </ul>
              )}
            </fieldset>
          )}

          <div className="form-actions">
            <Button variant="purple" disabled={!canAdd} onClick={handleAdd}>
              Add
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                resetForm()
                setFormOpen(false)
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      {/* `dc.html:4322-4323` — the opt-out sits AFTER the add affordance, and
       * only while nothing has been added (once a service exists the question
       * is already answered). Rendered whether or not the add-form is open,
       * matching the design's `showSvcAck` gate, which is independent of
       * `addingSvc`. */}
      {services.length === 0 && (
        <div className="rfq-services-ack">
          <Checkbox
            checked={noServicesAck}
            onChange={onNoServicesAckChange}
            label="No additional services needed for this request"
          />
        </div>
      )}
    </div>
  )
}
